import { BadgeProps } from "ui/components/Badge/types";
import {
  InvoiceSearchFormValues,
} from "./types";


export const initialInvoiceSearchForm: InvoiceSearchFormValues = {
  search: null,
  invoiceStatusId: null,
};

/**
 * Mapiranje statusa računa na badge variant prema fazi pipeline-a (sale i rent dijele iste ID-eve).
 * Konzistentno s OffersCard semantikom: pasivno=sivo, u tijeku=plavo, čeka uplatu=žuto, plaćeno=zeleno, otkazano=crveno.
 */
export const getInvoiceStatusBadgeVariant = (statusId: string | null | undefined): BadgeProps["variant"] => {
  switch (statusId) {
    case "draft":
      return "secondary";
    case "created":
      return "violet";
    case "fiscalized":
      return "info";
    case "sent":
      return "warning";
    case "paid":
      return "success";
    case "cancelled":
      return "danger";
    default:
      return "secondary";
  }
};

/**
 * Tailwind klasa za bar/circle indikator po statusu računa.
 */
export const getInvoiceStatusBarColor = (statusId: string | null | undefined): string => {
  switch (statusId) {
    case "draft":
      return "bg-slate-300";
    case "created":
      return "bg-slate-300";
    case "fiscalized":
      return "bg-blue-400";
    case "sent":
      return "bg-yellow-400";
    case "paid":
      return "bg-green-500";
    case "cancelled":
      return "bg-red-400";
    default:
      return "bg-slate-300";
  }
};

// Redoslijed statusa računa u pipeline-u. Nepoznati idu na kraj.
export const INVOICE_STATUS_ORDER = ["draft", "created", "fiscalized", "sent", "paid", "cancelled"];
export const invoiceStatusRank = (id: string) => {
  const i = INVOICE_STATUS_ORDER.indexOf(id);
  return i === -1 ? 999 : i;
};

/**
 * Prozirni gradient ispune po statusu — okrenut (jak na kraju, uz vertikalni border) -> blijedi prema početku.
 * Boje: draft = slate, created = violet, fiscalized = sky, sent = žuta, paid = emerald, cancelled = rose.
 * Pune klase za Tailwind JIT.
 */
export const getInvoiceStatusBarFill = (statusId: string | null | undefined): string => {
  switch (statusId) {
    case "fiscalized":
      return "bg-gradient-to-l from-sky-500/25 to-sky-500/[0.02] border-r-2 border-sky-500 group-hover/row:from-sky-500/40";
    case "sent":
      return "bg-gradient-to-l from-yellow-300/70 to-yellow-300/10 border-r-2 border-yellow-400 group-hover/row:from-yellow-300/85";
    case "paid":
      return "bg-gradient-to-l from-emerald-500/30 to-emerald-500/[0.02] border-r-2 border-emerald-500 group-hover/row:from-emerald-500/45";
    case "cancelled":
      return "bg-gradient-to-l from-rose-500/25 to-rose-500/[0.02] border-r-2 border-rose-500 group-hover/row:from-rose-500/40";
    case "created":
      return "bg-gradient-to-l from-violet-400/30 to-violet-400/[0.02] border-r-2 border-violet-400 group-hover/row:from-violet-400/45";
    case "draft":
    default:
      return "bg-gradient-to-l from-slate-400/30 to-slate-400/[0.02] border-r-2 border-slate-400 group-hover/row:from-slate-400/45";
  }
};

/**
 * Boja iznosa po statusu — samo terminalni ishodi (paid = zeleno, cancelled = crveno), ostalo neutralno.
 */
export const getInvoiceStatusAmountColor = (statusId: string | null | undefined): string => {
  switch (statusId) {
    case "paid":
      return "text-emerald-600";
    case "cancelled":
      return "text-rose-600";
    default:
      return "text-slate-800";
  }
};

/**
 * Boja ringa (hover) po statusu — usklađena s bojom ispune. Pune klase za Tailwind JIT.
 */
export const getInvoiceStatusRingColor = (statusId: string | null | undefined): string => {
  switch (statusId) {
    case "fiscalized":
      return "hover:ring-sky-400";
    case "sent":
      return "hover:ring-yellow-400";
    case "paid":
      return "hover:ring-emerald-400";
    case "cancelled":
      return "hover:ring-rose-400";
    case "created":
      return "hover:ring-violet-400";
    case "draft":
    default:
      return "hover:ring-slate-400";
  }
};
